/**
 * The photo gallery's standard look (the prototype's .o1Photos and .pv viewer).
 * Draws the behavior half's state and calls its actions; holds no rule. The
 * viewer's focus, key and scroll handling is drawing work and lives here.
 * Draws nothing when there is no photo.
 */
import { useEffect, useRef, useState, type MouseEvent, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';

import { partsOf, pieceRoot } from '../../contract/runtime';
import { Icon } from '../../ui/Icon';
import { Photo } from '../../ui/Photo';
import type { PhotoGalleryRuntime } from './usePhotoGallery';
import css from './PhotoGallery.module.css';
import { contract } from './contract';

const part = partsOf(contract);

/** Horizontal travel that turns a swipe into a photo change (the Mini App's lightbox value). */
const SWIPE = 48;

export function PhotoGalleryView({ state, actions, options }: PhotoGalleryRuntime) {
  const opener = useRef<HTMLElement | null>(null);
  const total = state.photos.length;
  if (total === 0) return null;
  const [main] = state.photos;

  const open = (index: number) => (e: MouseEvent<HTMLElement>) => {
    opener.current = e.currentTarget;
    actions.open(index);
  };

  return (
    <div className={css.photos} {...pieceRoot(contract)} {...part('gallery')}>
      <button
        type="button"
        className={`${css.shot} ${css.main}`}
        aria-label={`View photo 1 of ${total}`}
        onClick={open(0)}
        {...part('main')}
      >
        <Photo photo={main} className={css.shotImg} />
      </button>
      {state.thumbs.length > 0 && (
        <div className={css.thumbs} {...part('thumbs')}>
          {state.thumbs.map((t) => (
            <button
              key={`${t.photo.url}-${t.index}`}
              type="button"
              className={`${css.shot} ${css.thumb}`}
              aria-label={t.more > 0 ? `View all ${total} photos` : `View photo ${t.index + 1} of ${total}`}
              onClick={open(t.index)}
              {...part('thumb')}
            >
              <Photo photo={t.photo} loading="lazy" className={css.shotImg} />
              {t.more > 0 && (
                <span className={css.more} {...part('more')}>
                  +{t.more} more
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {state.viewerIndex !== null && (
        <Viewer
          state={state}
          actions={actions}
          options={options}
          onClosed={() => {
            if (opener.current && document.body.contains(opener.current)) opener.current.focus();
          }}
        />
      )}
    </div>
  );
}

function Viewer({ state, actions, onClosed }: PhotoGalleryRuntime & { onClosed: () => void }) {
  const root = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const startX = useRef<number | null>(null);
  const [dragX, setDragX] = useState(0);
  const index = state.viewerIndex ?? 0;
  const photo = state.photos[index];
  const total = state.photos.length;

  // Open: focus the close button and hold the page behind still; close: focus back on what opened it.
  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;
  useEffect(() => {
    closeBtn.current?.focus();
    const html = document.documentElement;
    const before = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      html.style.overflow = before;
      closedRef.current();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') actions.close();
      else if (e.key === 'ArrowLeft') actions.go(-1);
      else if (e.key === 'ArrowRight') actions.go(1);
      else if (e.key === 'Tab' && root.current) {
        // Keep focus inside the viewer while it is open.
        const items = [...root.current.querySelectorAll<HTMLElement>('button')];
        const edge = e.shiftKey ? items[0] : items[items.length - 1];
        const wrap = e.shiftKey ? items[items.length - 1] : items[0];
        if (!edge || !wrap || (document.activeElement !== edge && root.current.contains(document.activeElement))) return;
        e.preventDefault();
        wrap.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  const onTouchStart = (e: TouchEvent) => {
    startX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchMove = (e: TouchEvent) => {
    if (startX.current === null) return;
    setDragX((e.touches[0]?.clientX ?? startX.current) - startX.current);
  };
  const onTouchEnd = () => {
    if (startX.current === null) return;
    startX.current = null;
    if (dragX <= -SWIPE) actions.go(1);
    else if (dragX >= SWIPE) actions.go(-1);
    setDragX(0);
  };

  if (!photo) return null;
  return createPortal(
    <div ref={root} className={css.viewer} role="dialog" aria-modal="true" aria-label="Photo viewer" {...pieceRoot(contract)} {...part('viewer')}>
      <div className={css.back} onClick={actions.close} {...part('viewer-backdrop')} />
      <div
        className={`${css.stage} ${dragX ? css.dragging : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) actions.close();
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <img
          key={index}
          src={photo.url}
          alt=""
          draggable={false}
          style={dragX ? { transform: `translate3d(${dragX}px, 0, 0)` } : undefined}
          {...part('viewer-photo')}
        />
      </div>
      {state.hasPrev && (
        <button
          type="button"
          className={`${css.btn} ${css.prev}`}
          aria-label="Previous photo"
          onClick={() => actions.go(-1)}
          {...part('viewer-prev')}
        >
          <Icon name="left" />
        </button>
      )}
      {state.hasNext && (
        <button
          type="button"
          className={`${css.btn} ${css.next}`}
          aria-label="Next photo"
          onClick={() => actions.go(1)}
          {...part('viewer-next')}
        >
          <Icon name="right" />
        </button>
      )}
      <button
        ref={closeBtn}
        type="button"
        className={`${css.btn} ${css.close}`}
        aria-label="Close"
        onClick={actions.close}
        {...part('viewer-close')}
      >
        <Icon name="close" />
      </button>
      {total > 1 && (
        <div className={css.count} aria-live="polite" {...part('viewer-count')}>
          {index + 1} of {total}
        </div>
      )}
    </div>,
    document.body,
  );
}
